import React, { useState, FormEvent } from "react";
import "./Registration.scss";

type ApiResponse = {
  status?: boolean;
  message?: string;
  data?: any;
  error?: string | Record<string, any>;
};

export default function Registration() {
  const [firstName, setFirstName] = useState<string>("");
  const [lastName, setLastName] = useState<string>("");
  const [email, setEmail] = useState<string>("");
  const [password, setPassword] = useState<string>("");
  const [activeSubscription, setActiveSubscription] = useState<boolean>(false);

  const [loading, setLoading] = useState<boolean>(false);
  const [successMsg, setSuccessMsg] = useState<string | null>(null);
  const [errorMsg, setErrorMsg] = useState<string | null>(null);

  const [errors, setErrors] = useState<Record<string, string>>({});

  const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
  const passwordRegex = /^(?=.*[a-z])(?=.*[A-Z])(?=.*\d)(?=.*[^A-Za-z\d]).{8,}$/;

  const isFormValidQuick = (): boolean => {
    return (
      firstName.trim() !== "" &&
      lastName.trim() !== "" &&
      emailRegex.test(email) &&
      passwordRegex.test(password)
    );
  };

  const validate = (showErrors = true): boolean => {
    const next: Record<string, string> = {};

    if (!firstName.trim()) next.firstName = "First name is required.";
    if (!lastName.trim()) next.lastName = "Last name is required.";
    if (!email.trim()) next.email = "Email is required.";
    else if (!emailRegex.test(email)) next.email = "Enter a valid email address.";
    if (!password) next.password = "Password is required.";
    else if (!passwordRegex.test(password))
      next.password = "Password must be 8+ chars, include upper, lower, number and special char.";

    if (showErrors) setErrors(next);

    const valid = Object.keys(next).length === 0;
    if (!valid && showErrors) setErrorMsg("Please fix the highlighted errors.");
    else setErrorMsg(null);
    return valid;
  };

  const handleSubmit = async (e: FormEvent) => {
    e.preventDefault();
    setSuccessMsg(null);
    setErrorMsg(null);

    if (!validate()) return;

    setLoading(true);

    try {
      const res = await fetch(`${process.env.REACT_APP_API_BASE_URL}/auth/register`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          first_name: firstName,
          last_name: lastName,
          email,
          password,
        }),
      });

      const json: ApiResponse = await res.json();

      if (res.ok && json.status === true) {
        setSuccessMsg(json.message || "User registered successfully");

        setFirstName("");
        setLastName("");
        setEmail("");
        setPassword("");
        setActiveSubscription(false);
        setErrors({});

        setTimeout(() => {
          window.location.href = "/login";
        }, 2000);
      } else {
        setErrorMsg(
          (typeof json.error === "string" && json.error) ||
          json.message ||
          "Registration failed."
        );
      }
    } catch (err) {
      setErrorMsg("Network error. Please try again.");
    } finally {
      setLoading(false);
    }
  };


  const fillDummy = () => {
    setFirstName("Fathima");
    setLastName("Shameeea");
    setEmail("fathima@example.com");
    setPassword("P@ssw0rd123");
    setActiveSubscription(true);
    setErrors({});
    setErrorMsg(null);
    setSuccessMsg(null);
  };

  return (
    <div className="registration-page">
      <form className="reg-card" onSubmit={handleSubmit} noValidate>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
          <div className="heading-section">
            <h2>Sign Up</h2>
            <p className="subtext">Start your journey toward smarter productivity</p>
          </div>

          <button
            type="button"
            className="muted-btn"
            onClick={fillDummy}
            style={{ fontSize: '0.9rem' }}
            aria-label="Fill dummy data"
            title="Fill dummy data"
          />
        </div>

        {successMsg && <div className="success">{successMsg}</div>}
        {errorMsg && <div className="error">{errorMsg}</div>}

        <div className="input-row">
          <div className="input-group">
            <label>First name</label>
            <input
              type="text"
              placeholder="First name"
              value={firstName}
              onChange={(e) => {
                setFirstName(e.target.value);
                setErrors((p) => { const c = { ...p }; delete c.firstName; return c; });
              }}
            />
            {errors.firstName && <div className="field-error">{errors.firstName}</div>}
          </div>

          <div className="input-group">
            <label>Last name</label>
            <input
              type="text"
              placeholder="Last name"
              value={lastName}
              onChange={(e) => {
                setLastName(e.target.value);
                setErrors((p) => { const c = { ...p }; delete c.lastName; return c; });
              }}
            />
            {errors.lastName && <div className="field-error">{errors.lastName}</div>}
          </div>
        </div>

        <div className="input-group">
          <label>Email</label>
          <input
            type="email"
            placeholder="you@example.com"
            value={email}
            onChange={(e) => {
              setEmail(e.target.value);
              setErrors((p) => { const c = { ...p }; delete c.email; return c; });
            }}
          />
          {errors.email && <div className="field-error">{errors.email}</div>}
        </div>

        <div className="input-group">
          <label>Password</label>
          <input
            type="password"
            placeholder="Create a password"
            value={password}
            onChange={(e) => {
              const value = e.target.value;
              setPassword(value);

              if (!passwordRegex.test(value)) {
                setErrors((prev) => ({ ...prev, password: "Password must be 8+ chars, include upper, lower, number and special char." }));
              } else {
                setErrors((p) => {
                  const c = { ...p };
                  delete c.password;
                  return c;
                });
              }
            }}

          />
          <small className="hint">Minimum 8 characters, include upper, lower, number & special char</small>
          {errors.password && <div className="field-error">{errors.password}</div>}
        </div>

        <div className="input-group checkbox-group">
          <label style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
            <input
              type="checkbox"
              checked={activeSubscription}
              onChange={(e) => setActiveSubscription(e.target.checked)}
            />
            <span>Active subscription</span>
          </label>
          <small className="hint">If checked, user will start with an active subscription</small>
        </div>

        <button className="primary-btn" type="submit" disabled={loading || !isFormValidQuick()}>
          {loading ? "Registering..." : "Create account"}
        </button>

        <div className="muted">
          By creating an account you agree to our <a href="#">Terms</a>.
        </div>
      </form>
    </div>
  );
}


